import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/constants';

interface ContentIdeasRequest {
  brandId: string;
  personaId: string;
  appLanguage?: string;
  debug?: boolean;
  excludeIdeas?: string[]; // New parameter to exclude existing ideas
}

interface ContentIdeasResponse {
  success?: boolean;
  ideas?: any[]; // Direct array of ideas without nesting
  data?: {
    ideas: any[]; // Nested array of ideas for backward compatibility
  };
  timestamp?: string;
  error?: string;
  code?: string;
  reason?: string;
  status?: number;
}

// Helper function to normalize language to 2-letter code (e.g., "fr-FR" -> "fr", "en-US" -> "en")
const normalizeLanguage = (lang: string | undefined | null): string => {
  if (!lang) return 'en';
  // Extract first 2 characters and convert to lowercase
  const normalized = lang.substring(0, 2).toLowerCase();
  return normalized || 'en';
};

// Helper function to get authenticated token with retry
const getAuthToken = async (maxRetries = 3): Promise<string> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }
      
      const token = await user.getIdToken(true); // Force refresh to get fresh token
      if (!token) {
        throw new Error('Failed to get authentication token');
      }
      
      return token;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn(`Authentication attempt ${i + 1} failed:`, errorMessage);
      
      if (i === maxRetries - 1) {
        throw new Error('Authentication failed after multiple attempts');
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
  
  throw new Error('Authentication failed');
};

// Helper function to make API request with timeout and retry
const makeContentIdeasRequest = async (
  url: string, 
  body: any, 
  token: string, 
  timeout = 30000
): Promise<ContentIdeasResponse> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const requestHeaders = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    
    const startTime = performance.now();
    
    const response = await fetch(url, {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify(body),
      signal: controller.signal
    });
    
    const endTime = performance.now();
    const requestDuration = endTime - startTime;
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API error response:', response.status, errorText);
      
      // Try to parse error response
      let errorData: any = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (parseError) {
        console.warn('⚠️ Could not parse error response as JSON');
        errorData = { error: errorText };
      }
      
      // Return structured error response instead of throwing
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        code: errorData.code || 'api_error',
        reason: errorData.reason,
        status: response.status,
        ideas: [],
        data: { ideas: [] }
      };
    }
    
    const data = await response.json();
    
    // Normalize response format
    const ideas = data.ideas || data.data?.ideas || [];
    
    return {
      success: true,
      ideas,
      data: { ideas },
      timestamp: new Date().toISOString()
    };
    
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('❌ Request timed out after', timeout, 'ms');
      return {
        success: false,
        error: 'Request timed out. Please try again.',
        code: 'timeout_error',
        ideas: [],
        data: { ideas: [] }
      };
    }
    
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('❌ Network error:', errorMessage);
    console.error('❌ Full error object:', error);
    
    return {
      success: false,
      error: errorMessage || 'Network error occurred',
      code: 'network_error',
      ideas: [],
      data: { ideas: [] }
    };
  }
};

export const useContentIdeas = (params: ContentIdeasRequest) => {
  const { brandId, personaId, appLanguage, debug = false, excludeIdeas } = params;
  
  // Delay the first fetch until both IDs are certainly propagated to avoid race conditions.
  const [canFetchIdeas, setCanFetchIdeas] = useState(false);

  useEffect(() => {
    if (brandId && personaId) {
      // Wait a short moment to allow React state/contexts to settle.
      const timeout = setTimeout(() => {
        setCanFetchIdeas(true);
      }, 100);

      return () => clearTimeout(timeout);
    }

    // Reset if either ID becomes falsy.
    setCanFetchIdeas(false);
  }, [brandId, personaId]);
  
  return useQuery<ContentIdeasResponse>({
    queryKey: ['contentIdeas', brandId, personaId, appLanguage, debug, excludeIdeas],
    enabled: canFetchIdeas,
    staleTime: 2 * 60 * 1000, // Reduced to 2 minutes for fresher data
    gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes (renamed from cacheTime)
    // Prevent unexpected background refetches when user focuses the tab
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Authentication') || errorMessage.includes('401')) {
        return false;
      }
      // Retry up to 3 times for other errors with longer delays for race conditions
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 45000), // Longer exponential backoff for race conditions
    // Disable automatic polling by default to avoid periodic calls every 30-50s.
    // Keep an opt-in debug mode to help during development if needed.
    refetchInterval: debug
      ? (data) => {
          if (data && (data as ContentIdeasResponse).success) return false;
          return 30000; // only when debug=true and last fetch failed
        }
      : false,
    refetchIntervalInBackground: false, // no background polling
    queryFn: async (): Promise<ContentIdeasResponse> => {
      try {
        // Debug: Track how many times the queryFn runs in development
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.count('🎯 contentIdeas queryFn invoked');
        }

        // Add a small random delay to prevent race conditions when multiple components mount
        const randomDelay = Math.random() * 1000; // 0-1 second random delay
        await new Promise(resolve => setTimeout(resolve, randomDelay));
        
        // Get authenticated token
        const token = await getAuthToken();
        
        const normalizedLang = normalizeLanguage(appLanguage);
        console.log('🌍 useContentIdeas: Sending appLanguage to API:', normalizedLang, 'from input:', appLanguage);
        
        const requestBody: any = {
          brandId,
          personaId,
          appLanguage: normalizedLang
        };
        
        // Add excludeIdeas parameter if provided
        if (excludeIdeas && excludeIdeas.length > 0) {
          requestBody.excludeIdeas = excludeIdeas;
        }
        
        console.log('🌍 useContentIdeas: Request body:', JSON.stringify(requestBody, null, 2));
        
        const result = await makeContentIdeasRequest(
          `${API_BASE_URL}/ai-coach/content/ideas`,
          requestBody,
          token,
          30000 // 30 second timeout
        );
        
        // If the request was successful, return the result
        if (result.success) {
          return result;
        }
        
        // If there was an error but we got a structured response, return it
        // This allows the UI to handle errors gracefully
        return result;
        
      } catch (error) {
        console.error('❌ Fatal error in content ideas query:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        // Return a structured error response
        return {
          success: false,
          error: errorMessage || 'An unexpected error occurred',
          code: 'unknown_error',
          ideas: [],
          data: { ideas: [] }
        };
      }
    }
  });
};

/**
 * Hook for managing content ideas with accumulation and exclusion logic
 * This hook maintains a list of all ideas and handles generating more ideas
 * while excluding previously generated ones
 */
export const useContentIdeasWithAccumulation = (params: { brandId: string; personaId: string; appLanguage?: string }) => {
  const { brandId, personaId, appLanguage } = params;
  const normalizedAppLanguage = normalizeLanguage(appLanguage);
  console.log('🌍 useContentIdeasWithAccumulation: Received appLanguage:', appLanguage, 'normalized:', normalizedAppLanguage);
  
  const [allIdeas, setAllIdeas] = useState<any[]>([]);
  const [isGeneratingMore, setIsGeneratingMore] = useState(false);
  
  // Add ref to track if we're currently fetching to prevent race conditions
  const isFetchingRef = useRef(false);
  
  // Initial ideas fetch (without exclusions)
  const initialIdeasQuery = useContentIdeas({
    brandId,
    personaId,
    appLanguage: normalizedAppLanguage
  });

  // HTTP failures are returned as data, so React Query does not retry a denied
  // generation. Expose them to the form's error UI as well as transport errors.
  const ideasError = useMemo(() => {
    if (initialIdeasQuery.error) return initialIdeasQuery.error;
    const result = initialIdeasQuery.data;
    if (result?.success !== false) return null;
    return Object.assign(new Error(result.error || 'Failed to load content ideas'), {
      code: result.code,
      reason: result.reason,
      status: result.status,
    });
  }, [initialIdeasQuery.error, initialIdeasQuery.data]);
  
  // Update allIdeas when initial ideas are loaded
  useEffect(() => {
    const data = initialIdeasQuery.data as ContentIdeasResponse | undefined;
    if (data?.success && (data?.data?.ideas || data?.ideas)) {
      const newIdeas = data?.data?.ideas || data?.ideas || [];
      setAllIdeas(newIdeas);
    } else if (data && data.success === false) {
      // Don't reset allIdeas on error to preserve any existing ideas
    }
  }, [initialIdeasQuery.data]);
  
  // Function to generate more ideas
  const generateMoreIdeas = useCallback(async () => {
    if (isGeneratingMore || !brandId || !personaId || isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    setIsGeneratingMore(true);
    
    try {
      const token = await getAuthToken();
      
      // Extract titles from existing ideas for exclusion
      const excludeIdeas = allIdeas.map(idea => 
        typeof idea === 'object' && idea !== null ? idea.title : idea
      ).filter(Boolean); // Remove any null/undefined values
      
      console.log('🌍 generateMoreIdeas: Using normalizedAppLanguage:', normalizedAppLanguage, 'from params:', appLanguage);
      
      const requestBody: any = {
        brandId,
        personaId,
        appLanguage: normalizedAppLanguage
      };
      
      if (excludeIdeas.length > 0) {
        requestBody.excludeIdeas = excludeIdeas;
      }
      
      console.log('🌍 generateMoreIdeas: Request body:', JSON.stringify(requestBody, null, 2));
      
      const result = await makeContentIdeasRequest(
        `${API_BASE_URL}/ai-coach/content/ideas`,
        requestBody,
        token,
        30000
      );
      
      if (result.success) {
        const newIdeas = result.ideas || result.data?.ideas || [];
        
        // Add new ideas to existing ones
        setAllIdeas(prevIdeas => {
          const combinedIdeas = [...prevIdeas, ...newIdeas];
          return combinedIdeas;
        });
      } else {
        throw new Error(result.error || 'Failed to generate more ideas');
      }
      
    } catch (error) {
      console.error('❌ Error generating more ideas:', error);
      throw error;
    } finally {
      isFetchingRef.current = false;
      setIsGeneratingMore(false);
    }
  }, [brandId, personaId, appLanguage, allIdeas, isGeneratingMore]);
  
  // Reset ideas when brand or persona changes
  useEffect(() => {
    if (brandId && personaId) {
      setAllIdeas([]);
      isFetchingRef.current = false; // Reset fetch state
    }
  }, [brandId, personaId]);
  
  return {
    ideas: allIdeas,
    isLoading: initialIdeasQuery.isLoading,
    isGeneratingMore,
    error: ideasError,
    generateMoreIdeas,
    refetch: initialIdeasQuery.refetch
  };
};
